import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { featureRequestKeys } from "@/application/query-keys/feature-requests.keys";
import {
  featureRequestCommentsRepository,
  type CreateCommentInput,
} from "@/infrastructure/repositories/feature-request-comments.supabase.repository";
import { featureRequestAttachmentsRepository } from "@/infrastructure/repositories/feature-request-attachments.supabase.repository";
import type { FeatureRequestAttachmentRow } from "@/infrastructure/supabase/database.types";

// ── comments ──────────────────────────────────────────────────────────────
export function useRequestComments(requestId: string | null) {
  return useQuery({
    queryKey: featureRequestKeys.comments(requestId ?? ""),
    queryFn: () => featureRequestCommentsRepository.listForRequest(requestId as string),
    enabled: !!requestId,
  });
}

export function useAddComment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateCommentInput) => featureRequestCommentsRepository.create(input),
    onSuccess: (row) => {
      void queryClient.invalidateQueries({ queryKey: featureRequestKeys.comments(row.request_id) });
    },
  });
}

export function useDeleteComment(requestId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => featureRequestCommentsRepository.remove(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: featureRequestKeys.comments(requestId) });
    },
  });
}

// ── attachments ───────────────────────────────────────────────────────────
export function useRequestAttachments(requestId: string | null) {
  return useQuery({
    queryKey: featureRequestKeys.attachments(requestId ?? ""),
    queryFn: () => featureRequestAttachmentsRepository.listForRequest(requestId as string),
    enabled: !!requestId,
  });
}

export function useUploadAttachment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      requestId,
      file,
      uploadedBy,
    }: {
      requestId: string;
      file: File;
      uploadedBy: string;
    }) => featureRequestAttachmentsRepository.upload(requestId, file, uploadedBy),
    onSuccess: (row) => {
      void queryClient.invalidateQueries({
        queryKey: featureRequestKeys.attachments(row.request_id),
      });
    },
  });
}

export function useDeleteAttachment(requestId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (attachment: Pick<FeatureRequestAttachmentRow, "id" | "storage_path">) =>
      featureRequestAttachmentsRepository.remove(attachment),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: featureRequestKeys.attachments(requestId) });
    },
  });
}
